import React from "react"
import PropTypes from "prop-types"
import cx from "classnames"
import Icon from "../icon"
import Button from "../button"

export const types = {
  success: {
    bg: "bg-green-100",
    iconColor: "text-green-700",
    icon: "check-circle",
    buttonColor: "bg-green-400 text-black hover:bg-green-500 focus:bg-green-500"
  },
  warning: {
    bg: "bg-yellow-100",
    iconColor: "text-yellow-700",
    icon: "alert-circle",
    buttonColor: "bg-yellow-400 hover:bg-yellow-400"
  },
  info: {
    bg: "bg-blue-100",
    iconColor: "text-blue-700",
    icon: "information-circle",
    buttonColor: "bg-blue-400 hover:bg-blue-500"
  },
  error: {
    bg: "bg-red-100",
    iconColor: "text-red-700",
    icon: "alert-triangle",
    buttonColor: "bg-red-400 hover:bg-red-500"
  }
}

const sizes = {
  sm: "py-2 px-4 items-center",
  lg: "p-4"
}

export default function Note({
  type = "info",
  size = "lg",
  title,
  message,
  action,
  children,
  icon = true,
  ...props
}) {
  const className = cx(
    `rounded-lg text-sm text-black flex`,
    types[type].bg,
    sizes[size],
    props.className
  )

  const iconName = typeof icon === "string" ? icon : types[type].icon

  return (
    <div className={className} data-testid="note" style={{ minHeight: "48px" }}>
      {icon && (
        <Icon name={iconName} className={`${types[type].iconColor} mr-4`} />
      )}
      <div className={cx("w-full", size === "sm" && "flex items-center")}>
        {size !== "sm" && title && (
          <Title className="flex-shrink-0">{title}</Title>
        )}
        {message && (
          <p
            className={cx("text-black leading-6", size === "sm" && "flex-grow")}
          >
            {message}
          </p>
        )}
        {action && (
          <Button
            size="sm"
            is="plain"
            className={cx(types[type].buttonColor, {
              "mt-4": size !== "sm",
              "ml-4": size === "sm"
            })}
            onClick={action.onClick}
            href={action.href}
            target={action.target}
          >
            {action.label}
          </Button>
        )}
        {children}
      </div>
    </div>
  )
}

Note.propTypes = {
  type: PropTypes.oneOf(["info", "success", "warning", "error"]),
  size: PropTypes.oneOf(["sm", "lg"]),
  /** Only available on `lg` size */
  title: PropTypes.string,
  message: PropTypes.string,
  action: PropTypes.shape({
    label: PropTypes.string,
    onClick: PropTypes.func,
    href: PropTypes.string,
    target: PropTypes.string
  }),
  /** Controls if an icon is displayed or not. */
  icon: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ])
}

export function Title({ children, ...props }) {
  const className = cx(
    "font-semibold text-sm text-black leading-6 mb-1",
    props.className
  )

  return <div className={className}>{children}</div>
}
